import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Monitor, Heart, Brain, Moon, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const OmaghAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Omagh | Community Stress Support</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy for Omagh and West Tyrone. Online sessions available - no travel required. Trauma-informed care, community resilience support. CNHC registered."
        />
        <meta name="keywords" content="anxiety treatment Omagh, stress management Omagh, online hypnotherapy Tyrone, trauma-informed therapy Omagh, community anxiety support" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/omagh-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Omagh | Community Stress Support" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy for Omagh. Online sessions available - no travel required. Trauma-informed care supporting community resilience." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety Treatment Omagh",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Omagh and County Tyrone via online sessions. Specializing in trauma-informed care, community resilience, anxiety disorders, and chronic stress. Supporting western communities with accessible online therapy.",
            "url": "https://www.sharonclare-hypnotherapy.com/omagh-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Omagh",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Tyrone"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Do you offer online anxiety treatment for Omagh residents?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. I offer secure online hypnotherapy sessions via video call, which is the recommended option for Omagh residents given the distance to our physical clinics. Online sessions provide exactly the same professional, evidence-based anxiety treatment as in-person sessions. You receive the full benefit of hypnotherapy from the comfort and privacy of your home in Omagh, without any travel required."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with community trauma and historical stress?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, solution-focused hypnotherapy can be very effective for trauma and stress related to community experiences. I have a deep understanding of the Northern Ireland context and the ongoing impact of historical events on individuals and communities. The solution-focused approach works by helping you build resilience, reduce anxiety, improve sleep, and move forward while honoring your experiences. It's about supporting healing and creating a calmer future."
                }
              },
              {
                "@type": "Question",
                "name": "Is online hypnotherapy as effective as in-person sessions?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, research and clinical experience show that online hypnotherapy is equally as effective as in-person sessions. The hypnotherapy process works the same way via video call - you experience the same deep relaxation, receive the same evidence-based treatment, and achieve the same outcomes. Many clients actually prefer online sessions for the convenience, privacy, and ability to attend from the comfort of their own home."
                }
              },
              {
                "@type": "Question",
                "name": "Do you work with GPs in the Omagh area?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, I'm happy to collaborate with your GP or other healthcare providers in Omagh. While hypnotherapy doesn't require a GP referral, I recognize that many clients benefit from coordinated care. I can provide updates to your GP (with your consent) about your progress and work alongside any existing medical or psychological treatment you're receiving. Hypnotherapy complements conventional healthcare rather than replacing it."
                }
              },
              {
                "@type": "Question",
                "name": "How many sessions will I need for anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients see significant improvements within 4-6 sessions for anxiety and stress. The number varies based on your individual circumstances, severity of symptoms, and goals. We'll discuss your specific needs during the free initial consultation and create a tailored treatment plan. I offer a discounted 4-session package which provides better value and ensures commitment to the process."
                }
              },
              {
                "@type": "Question",
                "name": "What if I'm in crisis or need urgent support?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy is not an emergency service. If you're in crisis or experiencing thoughts of self-harm, please contact your GP immediately, attend A&E at South West Acute Hospital in Enniskillen, or call the Lifeline crisis helpline (0808 808 8000, available 24/7). These services provide immediate support. Hypnotherapy is designed for ongoing anxiety management and building resilience, and works best when you're stable and able to engage with the therapeutic process."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety and Stress Treatment for Omagh
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy for Omagh and West Tyrone via secure online sessions
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Monitor className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Online sessions recommended</span> - No travel required
                </p>
              </div>
              <p className="text-gray-600 mt-2">Professional anxiety treatment from the comfort of your home in Omagh</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety in Omagh */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety and Stress in Omagh
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Omagh or the wider County Tyrone area and struggling with anxiety or stress, you're not alone. As the county town of Tyrone with a population of around 21,000, Omagh is a resilient community that has shown remarkable strength through challenging times. Like many western Northern Ireland towns, Omagh faces unique pressures - economic uncertainty, limited access to specialist services, and the ongoing impact of historical trauma on individuals and communities.
              </p>
              <p>
                Many Omagh residents experience anxiety related to various sources - work stress, financial worries, family responsibilities, health concerns, or the weight of community experiences that continue to affect wellbeing even years later. The challenge of accessing specialist mental health support in rural and western areas can leave people feeling isolated and without help. However, online hypnotherapy offers a solution - professional, evidence-based anxiety treatment accessible from the comfort and privacy of your home in Omagh, without the need for long journeys to Belfast or other distant clinics.
              </p>
              <p>
                Given the distance from Omagh to our physical clinics (approximately 68 miles to Belfast), I strongly recommend online sessions for Omagh residents. Secure video call sessions provide exactly the same professional hypnotherapy treatment as in-person appointments, with the significant advantage of zero travel time, increased privacy, and the ability to attend from your own home. Research and clinical experience show that online hypnotherapy is equally as effective as face-to-face sessions - you receive the same evidence-based treatment and achieve the same positive outcomes.
              </p>
              <p>
                As a CNHC registered hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I understand the specific challenges that Omagh residents face. I'm sensitive to the historical context of the area and approach treatment with the respect and understanding that community experiences deserve. My focus is on supporting resilience, healing, and moving forward - helping you build a calmer, more hopeful future while honoring your experiences and the strength of your community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Anxiety Affects You */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Anxiety and Stress Affect Omagh Residents
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Common Anxiety Symptoms</h3>
                <ul className="space-y-3">
                  {[
                    'Constant worry and racing thoughts',
                    'Difficulty falling or staying asleep',
                    'Physical symptoms: tight chest, pounding heart, tension',
                    'Avoiding situations that trigger anxiety',
                    'Feeling on edge or unable to relax',
                    'Difficulty concentrating or making decisions',
                    'Irritability and mood swings',
                    'Feeling disconnected or numb'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Community-Specific Stressors</h3>
                <ul className="space-y-3">
                  {[
                    'Historical trauma and community experiences',
                    'Limited access to specialist mental health support',
                    'Economic uncertainty in rural areas',
                    'Family and intergenerational stress',
                    'Isolation from limited public transport',
                    'Health anxiety and medical access concerns',
                    'Financial pressures and employment stress',
                    'Caring responsibilities for family members'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 mt-6">
              <p className="text-gray-700 text-lg">
                If anxiety is affecting your sleep, relationships, work, or quality of life, you don't have to struggle alone. Solution-focused hypnotherapy offers an evidence-based approach that addresses both the symptoms and the underlying causes of anxiety, helping you build resilience and find peace.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Helps with Anxiety and Stress
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Solution-focused hypnotherapy gently shifts focus away from past trauma and toward healing and hope. Hypnosis calms the overactive fight-or-flight response, reduces anxiety, and helps you build resilience for a calmer future.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  icon: <Heart className="w-8 h-8 text-accent-600" />,
                  title: 'Calm the Nervous System',
                  text: 'Soothe the overactive stress response and reduce physical anxiety symptoms. Your body learns to relax and feel safe again, even when carrying difficult experiences.'
                },
                {
                  icon: <Brain className="w-8 h-8 text-accent-600" />,
                  title: 'Build Resilience',
                  text: 'Develop strength and coping strategies to handle life\'s challenges. Replace anxious patterns with healthier responses that support healing and forward movement.'
                },
                {
                  icon: <Moon className="w-8 h-8 text-accent-600" />,
                  title: 'Restore Restful Sleep',
                  text: 'Quiet the racing mind and restore healthy sleep patterns. Wake feeling more refreshed and better equipped to handle daily challenges with calm and clarity.'
                }
              ].map(({ icon, title, text }) => (
                <div key={title} className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">{icon}</div>
                    <h3 className="font-heading font-semibold text-gray-900 mb-3 text-xl">{title}</h3>
                    <p className="text-gray-700">{text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">What Hypnotherapy Can Help With</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2">
                  {[
                    'Generalized anxiety and chronic worry',
                    'Trauma-related anxiety and stress',
                    'Panic attacks and acute anxiety episodes',
                    'Sleep problems and nightmares'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <ul className="space-y-2">
                  {[
                    'Health anxiety and medical stress',
                    'Social anxiety and isolation',
                    'Work-related stress and burnout',
                    'Family and relationship stress'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Choose Sharon Clare Hypnotherapy for Omagh?
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  CNHC Registered Trauma-Informed Care
                </h3>
                <p className="text-gray-700">
                  As a CNHC registered clinical hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I bring professional healthcare expertise to every anxiety treatment session. I understand the Northern Ireland context and approach treatment with sensitivity, respect, and a deep awareness of how historical community experiences can affect individual wellbeing.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Accessible Online Sessions
                </h3>
                <p className="text-gray-700">
                  Online sessions via secure video call provide professional anxiety treatment without requiring the long journey to Belfast or other distant clinics. You receive the same evidence-based hypnotherapy from the comfort and privacy of your home in Omagh. This accessibility means specialist care is available to western communities without the barriers of distance and travel time.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Supporting Community Resilience
                </h3>
                <p className="text-gray-700">
                  I understand that Omagh is a community that has shown remarkable strength and resilience through challenging times. My approach focuses on honoring that resilience while supporting healing and moving forward. Solution-focused hypnotherapy is about building hope and creating a calmer future, not dwelling on past trauma or exploiting difficult experiences.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Collaborative Healthcare Approach
                </h3>
                <p className="text-gray-700">
                  I'm happy to work alongside your GP or other healthcare providers in Omagh. While hypnotherapy doesn't require a referral, many clients benefit from coordinated care. I can provide updates to your GP (with your consent) and work as part of your broader healthcare team. Hypnotherapy complements conventional healthcare rather than replacing it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Online Sessions Information */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Online Sessions for Omagh Residents
            </h2>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Monitor className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">Recommended Approach for Omagh</h3>
                  <p className="text-gray-700 mb-4">
                    Given the distance from Omagh to our physical clinics (approximately 68 miles to Belfast), I strongly recommend online sessions via secure video call. Online hypnotherapy provides:
                  </p>
                  <ul className="space-y-2">
                    {[
                      'No travel time or costs - attend from home',
                      'Increased privacy and comfort in your own space',
                      'Same professional, evidence-based treatment as in-person',
                      'Flexibility to schedule around work and family',
                      'Equal effectiveness to face-to-face sessions'
                    ].map((item) => (
                      <li key={item} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">What You Need for Online Sessions</h3>
                <p className="text-gray-700">
                  A device with camera and microphone (smartphone, tablet, or computer), a quiet private space, and a stable internet connection. Sessions are conducted via secure video call platform. Full instructions provided when you book.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 border-2 border-accent-200">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg flex items-center">
                <Shield className="w-6 h-6 text-accent-600 mr-2" />
                Crisis Support Resources
              </h3>
              <p className="text-gray-700 mb-4">
                Hypnotherapy is not an emergency service. If you're in crisis or experiencing thoughts of self-harm, please contact:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li><span className="font-semibold">Lifeline:</span> 0808 808 8000 (24/7 crisis helpline)</li>
                <li><span className="font-semibold">Your GP:</span> For urgent mental health concerns</li>
                <li><span className="font-semibold">A&E:</span> South West Acute Hospital, Enniskillen (if immediate medical help needed)</li>
              </ul>
              <p className="text-gray-700 mt-4 text-sm">
                Hypnotherapy works best when you're stable and able to engage with the therapeutic process. These crisis services provide immediate support when needed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What West Tyrone Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Living in Omagh, I'd struggled with anxiety for years but couldn't face the long journey to Belfast for regular appointments. Sharon's online sessions were exactly what I needed - professional treatment from home, no travel stress. After six sessions, my anxiety is manageable, I'm sleeping properly, and I feel hopeful about the future for the first time in a long time. The online format worked brilliantly."
                </p>
                <p className="font-semibold text-gray-900">M.C., Omagh - Generalized Anxiety</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I was having panic attacks and nightmares, feeling constantly on edge. Sharon's approach was sensitive and respectful - she understood the context without making me relive difficult experiences. The solution-focused approach helped me build resilience and move forward. The panic attacks have stopped and I'm sleeping through the night. I'm grateful for accessible online sessions that made this treatment possible from Tyrone."
                </p>
                <p className="font-semibold text-gray-900">P.K., County Tyrone - Panic Attacks and Trauma-Related Anxiety</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working with Sharon online from Omagh, I've learned to manage my stress and anxiety in ways I never thought possible. She's professional, understanding, and the treatment is exactly the same quality as in-person would be. I'm calmer, I'm handling family pressures better, and I'm not consumed by worry anymore. The online sessions were convenient and gave me the privacy I needed."
                </p>
                <p className="font-semibold text-gray-900">S.H., Omagh - Family Stress and Chronic Anxiety</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Omagh Anxiety Treatment
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you offer online anxiety treatment for Omagh residents?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. I offer secure online hypnotherapy sessions via video call, which is the recommended option for Omagh residents given the distance to our physical clinics. Online sessions provide exactly the same professional, evidence-based anxiety treatment as in-person sessions. You receive the full benefit of hypnotherapy from the comfort and privacy of your home in Omagh, without any travel required.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with community trauma and historical stress?
                </h3>
                <p className="text-gray-700">
                  Yes, solution-focused hypnotherapy can be very effective for trauma and stress related to community experiences. I have a deep understanding of the Northern Ireland context and the ongoing impact of historical events on individuals and communities. The solution-focused approach works by helping you build resilience, reduce anxiety, improve sleep, and move forward while honoring your experiences. It's about supporting healing and creating a calmer future.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is online hypnotherapy as effective as in-person sessions?
                </h3>
                <p className="text-gray-700">
                  Yes, research and clinical experience show that online hypnotherapy is equally as effective as in-person sessions. The hypnotherapy process works the same way via video call - you experience the same deep relaxation, receive the same evidence-based treatment, and achieve the same outcomes. Many clients actually prefer online sessions for the convenience, privacy, and ability to attend from the comfort of their own home.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you work with GPs in the Omagh area?
                </h3>
                <p className="text-gray-700">
                  Yes, I'm happy to collaborate with your GP or other healthcare providers in Omagh. While hypnotherapy doesn't require a GP referral, I recognize that many clients benefit from coordinated care. I can provide updates to your GP (with your consent) about your progress and work alongside any existing medical or psychological treatment you're receiving. Hypnotherapy complements conventional healthcare rather than replacing it.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions will I need for anxiety?
                </h3>
                <p className="text-gray-700">
                  Most clients see significant improvements within 4-6 sessions for anxiety and stress. The number varies based on your individual circumstances, severity of symptoms, and goals. We'll discuss your specific needs during the free initial consultation and create a tailored treatment plan. I offer a discounted 4-session package which provides better value and ensures commitment to the process.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What if I'm in crisis or need urgent support?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is not an emergency service. If you're in crisis or experiencing thoughts of self-harm, please contact your GP immediately, attend A&E at South West Acute Hospital in Enniskillen, or call the Lifeline crisis helpline (0808 808 8000, available 24/7). These services provide immediate support. Hypnotherapy is designed for ongoing anxiety management and building resilience, and works best when you're stable and able to engage with the therapeutic process.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Start Your Journey to Healing and Hope"
        subtitle="Take the first step toward less anxiety, better sleep, and more peace. Book a free online consultation today and discover how hypnotherapy can support your resilience and wellbeing from the comfort of your home in Omagh."
        primaryButtonText="Book Free Consultation"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default OmaghAnxietyStress;
